import React, { useState, useMemo } from 'react';
import { ArrowsClockwise, X, WarningCircle } from '@phosphor-icons/react';

/**
 * Dialog used when converting a quote into an invoice (situation).
 * - The user enters, per line, the **cumulative** advancement target (0..100).
 * - The dialog shows the previous advancement (already billed in past
 *   situations) and the delta this invoice will bill.
 *
 * Props:
 * - quote: full quote object (with lines + unit_price/quantity/tva_rate)
 * - previousAdvancements: { [lineKey]: percent } already billed (default {})
 * - onClose, onConfirm(line_advancements_targets), submitting
 */
export const QuotePercentageDialog = ({ quote, previousAdvancements = {}, onClose, onConfirm, submitting }) => {
  const lineKey = (ln, idx) => String(ln.id || ln.line_id || idx);

  const previousFor = (ln, idx) => {
    const candidates = [String(ln.id || ''), String(ln.line_id || ''), String(idx)];
    let max = 0;
    for (const k of candidates) {
      if (k && previousAdvancements[k] != null) {
        const v = Number(previousAdvancements[k]);
        if (v > max) max = v;
      }
    }
    return Math.max(0, Math.min(100, max));
  };

  // pcts holds the cumulative target % per line. Default to previous (no new
  // billing) so the user has to actively raise it.
  const [pcts, setPcts] = useState(() => {
    const init = {};
    (quote.lines || []).forEach((ln, idx) => {
      const prev = previousFor(ln, idx);
      const k = lineKey(ln, idx);
      init[k] = prev > 0 ? prev : 100; // first situation -> default 100%, next -> previous
    });
    return init;
  });

  const [defaultPct, setDefaultPct] = useState(100);

  const setLinePct = (key, val) => {
    let v = parseFloat(val);
    if (isNaN(v)) v = 0;
    if (v < 0) v = 0;
    if (v > 100) v = 100;
    setPcts(p => ({ ...p, [key]: v }));
  };

  const applyToAll = (val) => {
    let v = parseFloat(val);
    if (isNaN(v)) v = 0;
    if (v < 0) v = 0;
    if (v > 100) v = 100;
    setDefaultPct(v);
    const next = {};
    (quote.lines || []).forEach((ln, idx) => {
      const k = lineKey(ln, idx);
      const prev = previousFor(ln, idx);
      // Don't go below previous when applying preset
      next[k] = Math.max(prev, v);
    });
    setPcts(next);
  };

  // Compute totals
  const { totalHt, totalTtc, totalDeltaHt, totalDeltaTtc, totalPrevHt, hasError, errorLines } = useMemo(() => {
    let ht = 0, ttc = 0, dHt = 0, dTtc = 0, prevHt = 0;
    const errs = [];
    (quote.lines || []).forEach((ln, idx) => {
      const k = lineKey(ln, idx);
      const target = Number(pcts[k] ?? 0);
      const prev = previousFor(ln, idx);
      const lineHt = Number(ln.unit_price || 0) * Number(ln.quantity || 0);
      const tva = Number(ln.tva_rate || 0);
      ht += lineHt * target / 100;
      ttc += (lineHt * target / 100) * (1 + tva / 100);
      const delta = target - prev;
      dHt += lineHt * delta / 100;
      dTtc += (lineHt * delta / 100) * (1 + tva / 100);
      prevHt += lineHt * prev / 100;
      if (target + 1e-9 < prev) errs.push(k);
    });
    return { totalHt: ht, totalTtc: ttc, totalDeltaHt: dHt, totalDeltaTtc: dTtc, totalPrevHt: prevHt, hasError: errs.length > 0, errorLines: errs };
  }, [pcts, quote.lines, previousAdvancements]);

  const isSecondSituation = Object.values(previousAdvancements).some(v => Number(v || 0) > 0);

  return (
    <div
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20 }}
      onClick={onClose}
      data-testid="convert-dialog-overlay"
    >
      <div className="card" style={{ width: '100%', maxWidth: '1100px', maxHeight: '92vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.05rem' }}>
              {isSecondSituation ? `Nouvelle situation - devis ${quote.number}` : `Facturer le devis ${quote.number}`}
            </h3>
            <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#5E5F66' }}>
              {isSecondSituation
                ? "Saisissez l'avancement cumule (%) par article. La facture portera uniquement le delta vs les situations precedentes."
                : "Saisissez un % d'avancement pour chaque article (defaut: 100%)."}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="convert-dialog-close"><X size={22} /></button>
        </div>

        <div style={{ padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderBottom: '1px solid #F0F1F3' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Avancement cumule a appliquer :</span>
          {[25, 50, 75, 100].map(v => (
            <button
              key={v}
              type="button"
              className={`btn btn-sm ${defaultPct === v ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => applyToAll(v)}
              data-testid={`convert-preset-${v}`}
            >{v}%</button>
          ))}
          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={defaultPct}
            onChange={(e) => applyToAll(e.target.value)}
            className="form-input"
            data-testid="convert-default-input"
            style={{ width: 80 }}
          />
          <span style={{ fontSize: '0.82rem', color: '#5E5F66' }}>%</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="convert-lines-table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ textAlign: 'right' }}>Prix HT</th>
                {isSecondSituation && <th style={{ textAlign: 'center' }}>Avt precedent</th>}
                <th style={{ textAlign: 'center', width: 130 }}>Avt cumule %</th>
                {isSecondSituation && <th style={{ textAlign: 'right' }}>Delta a facturer</th>}
                <th style={{ textAlign: 'right' }}>HT a facturer</th>
                <th style={{ textAlign: 'right' }}>TTC a facturer</th>
              </tr>
            </thead>
            <tbody>
              {(quote.lines || []).map((ln, idx) => {
                const k = lineKey(ln, idx);
                const target = Number(pcts[k] ?? 0);
                const prev = previousFor(ln, idx);
                const lineHt = Number(ln.unit_price || 0) * Number(ln.quantity || 0);
                const tva = Number(ln.tva_rate || 0);
                const delta = Math.max(0, target - prev);
                const billHt = lineHt * delta / 100;
                const billTtc = billHt * (1 + tva / 100);
                const isErr = errorLines.includes(k);
                return (
                  <tr key={k} style={isErr ? { background: '#FEEFEF' } : undefined}>
                    <td style={{ fontWeight: 600 }}>{ln.description}</td>
                    <td style={{ textAlign: 'right' }}>{lineHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
                    {isSecondSituation && (
                      <td style={{ textAlign: 'center', color: '#5E5F66', fontWeight: 600 }} data-testid={`convert-line-prev-${k}`}>
                        {prev.toFixed(0)}%
                      </td>
                    )}
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="number"
                        min={prev}
                        max="100"
                        step="1"
                        value={target}
                        onChange={(e) => setLinePct(k, e.target.value)}
                        className="form-input"
                        data-testid={`convert-line-pct-${k}`}
                        style={{ width: 90, textAlign: 'right', display: 'inline-block', borderColor: isErr ? '#DC2626' : undefined }}
                        title={isSecondSituation ? `Min ${prev}% (deja facture), max 100%` : 'Entre 0 et 100%'}
                      />
                    </td>
                    {isSecondSituation && (
                      <td style={{ textAlign: 'right', fontWeight: 700, color: delta > 0 ? '#1565C0' : '#9E9EA7' }}>
                        +{delta.toFixed(0)}%
                      </td>
                    )}
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{billHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: '#2E7D32' }}>{billTtc.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {isSecondSituation && (
                <tr style={{ background: '#FAFBFC', fontSize: '0.85rem', color: '#5E5F66' }}>
                  <td colSpan={2} style={{ textAlign: 'right' }}>Deja facture (situations precedentes)</td>
                  <td colSpan={3} style={{ textAlign: 'right' }} data-testid="convert-prev-ht">{totalPrevHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
                  <td colSpan={2}></td>
                </tr>
              )}
              <tr style={{ background: '#F4F5F7', fontWeight: 700 }}>
                <td colSpan={isSecondSituation ? 4 : 2} style={{ textAlign: 'right' }}>TOTAL CUMULE</td>
                {isSecondSituation && <td style={{ textAlign: 'right' }} data-testid="convert-total-delta-ht">+{totalDeltaHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>}
                <td style={{ textAlign: 'right' }} data-testid="convert-total-ht">{totalHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
                <td style={{ textAlign: 'right', color: '#2E7D32' }} data-testid="convert-total-ttc">{totalTtc.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR</td>
              </tr>
              {isSecondSituation && (
                <tr style={{ background: '#E8F5E8', fontWeight: 700, fontSize: '0.95rem' }}>
                  <td colSpan={isSecondSituation ? 4 : 2} style={{ textAlign: 'right' }}>NET DE LA SITUATION</td>
                  <td style={{ textAlign: 'right' }}></td>
                  <td style={{ textAlign: 'right', color: '#2E7D32' }} data-testid="convert-net-ht">{totalDeltaHt.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR HT</td>
                  <td style={{ textAlign: 'right', color: '#2E7D32' }} data-testid="convert-net-ttc">{totalDeltaTtc.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} EUR TTC</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>

        {hasError && (
          <div style={{ margin: '0 20px', padding: '8px 12px', background: '#FEEFEF', border: '1px solid #FCA5A5', borderRadius: 6, color: '#7F1D1D', display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem' }}>
            <WarningCircle size={18} weight="bold" />
            Au moins une ligne a un avancement cumule inferieur au deja facture. Corrigez avant de continuer.
          </div>
        )}

        <div style={{ padding: '12px 20px', borderTop: '1px solid #E2E2E6', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button className="btn btn-secondary" onClick={onClose} data-testid="convert-cancel-btn">Annuler</button>
          <button
            className="btn btn-primary"
            onClick={() => onConfirm(pcts)}
            disabled={submitting || hasError}
            data-testid="convert-confirm-btn"
          >
            <ArrowsClockwise size={16} /> {submitting ? 'Creation...' : (isSecondSituation ? 'Creer la situation' : 'Creer la facture')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuotePercentageDialog;
